import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Alert, Snackbar, AlertColor, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface AlertMessage {
  id: string;
  message: string;
  severity: AlertColor;
  duration?: number;
}

interface AlertContextType {
  showAlert: (message: string, severity?: AlertColor, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showSuccess: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

interface AlertProviderProps {
  children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alerts, setAlerts] = useState<AlertMessage[]>([]);

  const showAlert = (message: string | any, severity: AlertColor = 'info', duration: number = 6000) => {
    const id = Date.now().toString();
    // Ensure message is a string to prevent "Objects are not valid as a React child" errors
    const safeMessage = typeof message === 'string' ? message : JSON.stringify(message);
    
    const newAlert: AlertMessage = { id, message: safeMessage, severity, duration };
    
    setAlerts(prev => [...prev, newAlert]);

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        removeAlert(id);
      }, duration);
    }
  };

  const showError = (message: string, duration: number = 8000) => {
    showAlert(message, 'error', duration);
  };

  const showSuccess = (message: string, duration: number = 4000) => {
    showAlert(message, 'success', duration);
  };

  const showWarning = (message: string, duration: number = 6000) => {
    showAlert(message, 'warning', duration);
  };

  const showInfo = (message: string, duration: number = 6000) => {
    showAlert(message, 'info', duration);
  };

  const removeAlert = (id: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== id));
  };

  const contextValue: AlertContextType = {
    showAlert,
    showError,
    showSuccess,
    showWarning,
    showInfo,
  };

  return (
    <AlertContext.Provider value={contextValue}>
      {children}
      
      {/* Render alerts */}
      <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999 }}>
        {alerts.map((alert, index) => (
          <Snackbar
            key={alert.id}
            open={true}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            style={{ 
              position: 'static',
              marginTop: index > 0 ? 8 : 0,
              transform: 'none'
            }}
          >
            <Alert
              severity={alert.severity}
              variant="filled"
              action={
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={() => removeAlert(alert.id)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
              sx={{
                minWidth: '300px',
                maxWidth: '500px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                '& .MuiAlert-message': {
                  wordBreak: 'break-word'
                }
              }}
            >
              {alert.message}
            </Alert>
          </Snackbar>
        ))}
      </div>
    </AlertContext.Provider>
  );
};

export const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};
